import type { ClinicRelationship } from '@/entities/business-relationship/api/businessRelationship.api';
import { useBusinessRelationshipList } from '@/entities/business-relationship/hooks/useBusinessRelationships';
import { getRelationshipGroup } from '@/entities/business-relationship/lib/statusView';
import type {
  MonthlyStatus,
  PerformanceClinic,
} from '@/entities/performance/api/performance.api';
import {
  useAllPerformanceClinics,
  useClinicSummaries,
  useMonthlySummary,
} from '@/entities/performance/hooks/usePerformanceQueries';
import {
  getMonthInsights,
  INSIGHT_LABEL,
  type InsightKey,
  type MonthClinicRow,
} from '@/entities/performance/lib/dashboard';
import {
  formatChange,
  formatRatio,
  shiftYm,
  ymLabel,
} from '@/entities/performance/lib/performance';
import { ErrorState, LoadingBlock } from '@/shared/components/StateViews';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Icon } from '@/shared/components/ui/icon';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { formatWon, toUrlYm } from '@/shared/utils/format';
import { useUIStore } from '@/stores/uiStore';
import { Link } from '@tanstack/react-router';
import { useMemo, type ReactNode } from 'react';
import { KpiItem, KpiRow } from './Kpi';

/** 선택 월 상태 배지 (Badge & Chip Medium) */
const MonthBadge = ({
  status,
  current,
  hasUnregistered,
}: {
  status?: MonthlyStatus;
  current: boolean;
  hasUnregistered: boolean;
}) => {
  if (status === 'SETTLED')
    return (
      <Badge state="success" emphasis="strong" size="medium">
        정산 완료
      </Badge>
    );
  if (current)
    return hasUnregistered ? (
      <Badge state="error" emphasis="strong" size="medium">
        처방 미등록
      </Badge>
    ) : null;
  if (status === 'UNREGISTERED' || !status)
    return (
      <Badge state="disabled" emphasis="strong" size="medium">
        처방 미등록
      </Badge>
    );
  // Figma에 없는 상태 — 확인 중 · 정산 전은 노란 · 회색 배지로 둔다
  if (status === 'PENDING_CONFIRMATION')
    return (
      <Badge state="warning" emphasis="strong" size="medium">
        처방 확인 중
      </Badge>
    );
  return (
    <Badge state="grey" emphasis="strong" size="medium">
      정산 전
    </Badge>
  );
};

/* ───────────── 실적_테이블 (523:1134) ───────────── */

const Table = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col overflow-hidden rounded-12 border border-line-grey">
    {children}
  </div>
);

const ArrowCell = () => (
  <span className="flex size-8 shrink-0 items-center justify-center text-fg-light">
    <Icon name="nav-arrow-right-line-16" />
  </span>
);

const linkToClinic = (ym: string, clinicId: number) => ({
  to: ROUTE_PATHS.PERFORMANCE_MONTH,
  params: { yearMonth: toUrlYm(ym) },
  search: { clinicId },
});

/** 제목-등록 · 결과-등록 — 병의원명 | 처방 예상 · 정산 확정 · 정산 비율 · › */
const ResultTable = ({
  ym,
  rows,
  tags,
}: {
  ym: string;
  rows: {
    clinicId: number;
    clinicName: string;
    estimated: string;
    confirmed: string;
    ratio: string;
  }[];
  tags?: Map<number, string>;
}) => (
  <Table>
    <div className="flex h-[65px] items-center gap-4 bg-surface-grey px-6 py-4 text-body-15-sb text-fg-tertiary">
      <p className="min-w-0 flex-1">병의원명</p>
      <div className="flex min-w-0 flex-1 items-center gap-20">
        <p className="min-w-0 flex-1 text-right">처방 예상 금액</p>
        <p className="min-w-0 flex-1 text-right">정산 확정 금액</p>
        <p className="w-[55px] shrink-0 text-right whitespace-nowrap">
          정산 비율
        </p>
        <span className="size-8 shrink-0" />
      </div>
    </div>
    {rows.map((r) => (
      <Link
        key={r.clinicId}
        {...linkToClinic(ym, r.clinicId)}
        className="flex h-16 items-center gap-4 px-6 py-4 transition-colors outline-none hover:bg-surface-grey focus-visible:bg-surface-grey"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <p className="truncate text-body-15-sb text-fg-primary">
            {r.clinicName}
          </p>
          {tags?.get(r.clinicId) && (
            <Badge state="grey" size="small">
              {tags.get(r.clinicId)}
            </Badge>
          )}
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-20">
          <p className="min-w-0 flex-1 text-right text-body-15-r text-fg-tertiary">
            {r.estimated}
          </p>
          <p className="min-w-0 flex-1 text-right text-body-15-sb text-fg-primary">
            {r.confirmed}
          </p>
          <p className="w-[55px] shrink-0 text-right text-body-15-r text-fg-tertiary">
            {r.ratio}
          </p>
          <ArrowCell />
        </div>
      </Link>
    ))}
  </Table>
);

/** 제목-미등록 · 결과-미등록 — 미등록/등록 | 처방 예상 · 처방 이미지 · [처방 등록] · › */
const RegistrationTable = ({
  ym,
  title,
  rows,
}: {
  ym: string;
  title: string;
  rows: {
    relationship: ClinicRelationship;
    estimated: number;
    imageCount: number;
  }[];
}) => {
  const openUpload = useUIStore((s) => s.openPrescriptionUpload);
  return (
    <Table>
      <div className="flex h-[65px] items-center gap-4 bg-surface-grey px-6 py-4 text-body-15-sb text-fg-tertiary">
        <p className="min-w-0 flex-1">{title}</p>
        <div className="flex min-w-0 flex-1 items-center gap-12">
          <p className="w-[140px] shrink-0 text-right">처방 예상 금액</p>
          <p className="w-[140px] shrink-0 text-right">처방 이미지</p>
        </div>
      </div>
      {rows.map(({ relationship: r, estimated, imageCount }) => (
        <div
          key={r.relationshipId}
          className="flex h-16 items-center gap-4 px-6 py-4"
        >
          <Link
            {...linkToClinic(ym, r.clinicId)}
            className="min-w-0 flex-1 truncate text-body-15-sb text-fg-primary outline-none hover:underline focus-visible:underline"
          >
            {r.clinicName}
          </Link>
          <div className="flex min-w-0 flex-1 items-center gap-12">
            <p className="w-[140px] shrink-0 text-right text-body-15-r text-fg-tertiary">
              {estimated > 0 ? formatWon(estimated) : '-'}
            </p>
            <p className="w-[140px] shrink-0 text-right text-body-15-r text-fg-tertiary">
              {imageCount}장
            </p>
            {/* M-06 처방 등록 — 거래처 화면과 같은 팝업(PrescriptionUploadDialog)을 연다 */}
            <Button
              size="medium"
              onClick={() =>
                openUpload({
                  relationshipId: r.relationshipId,
                  clinicName: r.clinicName,
                  statusView: r.statusView,
                  viewingSettlementYearMonth: ym,
                })
              }
            >
              처방 등록
            </Button>
            <Link
              {...linkToClinic(ym, r.clinicId)}
              aria-label={`${r.clinicName} 실적 보기`}
              className="rounded-12 outline-none hover:bg-grey-100 focus-visible:bg-grey-100"
            >
              <ArrowCell />
            </Link>
          </div>
        </div>
      ))}
    </Table>
  );
};

/* ───────────── 패널 본문 ───────────── */

/** 병의원별 정산 비율 — 목록 응답에 있으면 그 값, 없으면 병의원별 월 요약 */
const useClinicRows = (
  ym: string,
  clinics: PerformanceClinic[],
  enabled: boolean,
): MonthClinicRow[] => {
  const missing = enabled
    ? clinics
        .filter((c) => c.settlementRatio === undefined && c.hasSettlement)
        .map((c) => c.clinicId)
    : [];
  const summaries = useClinicSummaries(ym, missing);
  return clinics.map((clinic) => {
    if (clinic.settlementRatio !== undefined)
      return { clinic, ratio: clinic.settlementRatio };
    const i = missing.indexOf(clinic.clinicId);
    return {
      clinic,
      ratio: i === -1 ? null : (summaries[i]?.data?.settlementRatio ?? null),
    };
  });
};

/** 정산 완료 달 — 월 요약 · 인사이트 4종 · 병의원별 표 */
const SettledMonth = ({
  ym,
  clinics,
}: {
  ym: string;
  clinics: PerformanceClinic[];
}) => {
  const summary = useMonthlySummary(ym);
  const prev = useAllPerformanceClinics(shiftYm(ym, -1));
  const rows = useClinicRows(ym, clinics, true);
  const insights = useMemo(
    () => getMonthInsights(rows, prev.data?.content),
    [rows, prev.data],
  );
  const sorted = [...rows].sort(
    (a, b) =>
      b.clinic.confirmedAmount - a.clinic.confirmedAmount ||
      b.clinic.estimatedAmount - a.clinic.estimatedAmount,
  );
  // 표의 행 태그 — 최고 실적은 위 카드로 충분해 붙이지 않는다 (Figma 523:1111)
  const tags = new Map<number, string>();
  (['rise', 'fall', 'lowRatio'] as InsightKey[]).forEach((key) => {
    const item = insights[key as Exclude<InsightKey, 'top'>];
    if (item && !tags.has(item.clinic.clinicId))
      tags.set(item.clinic.clinicId, INSIGHT_LABEL[key]);
  });
  const s = summary.data;

  return (
    <>
      <div className="flex flex-col overflow-hidden rounded-12 border border-line-grey">
        <KpiRow
          className="bg-surface-grey"
          items={[
            <KpiItem
              key="clinics"
              label="거래 병의원"
              value={`${clinics.length}개`}
            />,
            <KpiItem
              key="rx"
              label="처방 예상 금액"
              value={formatWon(s?.estimatedAmountPrescription)}
            />,
            <KpiItem
              key="confirmed"
              label="정산 확정 금액"
              value={formatWon(s?.confirmedAmountSettlement)}
            />,
            <KpiItem
              key="ratio"
              label="정산 비율"
              value={formatRatio(s?.settlementRatio)}
            />,
          ]}
        />
        <KpiRow
          items={[
            <KpiItem
              key="top"
              label={INSIGHT_LABEL.top}
              name={insights.top?.clinic.clinicName ?? '-'}
              value={
                insights.top
                  ? formatWon(insights.top.clinic.confirmedAmount)
                  : '-'
              }
              sub={
                insights.top?.share != null &&
                `전체의 ${Math.round(insights.top.share)}%`
              }
            />,
            <KpiItem
              key="rise"
              label={INSIGHT_LABEL.rise}
              name={insights.rise?.clinic.clinicName ?? '-'}
              value={insights.rise ? formatChange(insights.rise.rate) : '-'}
              valueClassName={insights.rise && 'text-fg-error'}
              sub={
                insights.rise &&
                `${formatWon(insights.rise.from)} → ${formatWon(insights.rise.to)}`
              }
            />,
            <KpiItem
              key="fall"
              label={INSIGHT_LABEL.fall}
              name={insights.fall?.clinic.clinicName ?? '-'}
              value={insights.fall ? formatChange(insights.fall.rate) : '-'}
              valueClassName={insights.fall && 'text-fg-info'}
              sub={
                insights.fall &&
                `${formatWon(insights.fall.from)} → ${formatWon(insights.fall.to)}`
              }
            />,
            <KpiItem
              key="low"
              label={INSIGHT_LABEL.lowRatio}
              name={insights.lowRatio?.clinic.clinicName ?? '-'}
              value={
                insights.lowRatio ? formatRatio(insights.lowRatio.ratio) : '-'
              }
              valueClassName={insights.lowRatio && 'text-fg-warning'}
              sub={
                insights.lowRatio && `평균 ${formatRatio(s?.settlementRatio)}`
              }
            />,
          ]}
        />
      </div>
      <ResultTable
        ym={ym}
        tags={tags}
        rows={sorted.map(({ clinic: c, ratio }) => ({
          clinicId: c.clinicId,
          clinicName: c.clinicName,
          estimated: formatWon(c.estimatedAmount),
          confirmed: c.hasSettlement ? formatWon(c.confirmedAmount) : '-',
          ratio: formatRatio(ratio),
        }))}
      />
    </>
  );
};

/**
 * 선택 월 패널 (M-08) — 그래프에서 고른 달
 * - 정산 완료: 월 요약 · 인사이트 · 병의원별 표
 * - 현재 정산월: 처방 등록 현황 (미등록 · 등록 거래처, 행마다 [처방 등록])
 * - 그 밖의 지난달(미등록 · 확인 중 · 정산 전): 병의원별 표 (값이 없으면 "-")
 * 표의 행을 누르면 그 달 병의원 상세(M-09)로 간다.
 */
export const MonthPanel = ({
  ym,
  status,
  current,
  canPrev,
  canNext,
  onMove,
}: {
  ym: string;
  status?: MonthlyStatus;
  /** 현재 정산월 — 처방 등록 현황을 보여준다 */
  current: boolean;
  canPrev: boolean;
  canNext: boolean;
  onMove: (diff: -1 | 1) => void;
}) => {
  const clinicsQuery = useAllPerformanceClinics(ym);
  const relationships = useBusinessRelationshipList();
  const clinics = useMemo(
    () => clinicsQuery.data?.content ?? [],
    [clinicsQuery.data],
  );

  // 처방 등록 대상 거래처 — 거래 중 · 거래처 승인
  const ongoing = useMemo(
    () =>
      (relationships.data?.items ?? []).filter(
        (r) => getRelationshipGroup(r.statusView) === 'ongoing',
      ),
    [relationships.data],
  );
  const byClinic = new Map(clinics.map((c) => [c.clinicId, c]));
  const registration = ongoing.map((r) => {
    const c = byClinic.get(r.clinicId);
    return {
      relationship: r,
      estimated: c?.estimatedAmount ?? 0,
      imageCount: c?.imageCount ?? 0,
    };
  });
  const unregistered = registration.filter((r) => r.imageCount === 0);
  const registered = registration.filter((r) => r.imageCount > 0);

  const loading =
    clinicsQuery.isPending || (current && relationships.isPending);
  const error = clinicsQuery.isError;

  let body: ReactNode;
  if (loading) body = <LoadingBlock className="h-[320px]" />;
  else if (error)
    body = <ErrorState onRetry={() => void clinicsQuery.refetch()} />;
  else if (status === 'SETTLED')
    body = <SettledMonth ym={ym} clinics={clinics} />;
  else if (current)
    body = (
      <>
        <p className="text-body-16-sb text-fg-primary">
          {unregistered.length > 0 ? (
            <>
              <span className="text-fg-error">{unregistered.length}개</span>의
              거래처가 처방 내역 등록이 필요해요.
            </>
          ) : (
            '모든 거래처의 처방 내역이 등록되었어요.'
          )}
        </p>
        <div className="flex flex-col gap-10">
          {unregistered.length > 0 && (
            <RegistrationTable ym={ym} title="미등록" rows={unregistered} />
          )}
          {registered.length > 0 && (
            <RegistrationTable ym={ym} title="등록" rows={registered} />
          )}
        </div>
      </>
    );
  else {
    // 지난달 — 실적이 있는 병의원, 없으면 거래 중 거래처를 "-"로
    const rows =
      clinics.length > 0
        ? clinics.map((c) => ({
            clinicId: c.clinicId,
            clinicName: c.clinicName,
            estimated:
              c.estimatedAmount > 0 ? formatWon(c.estimatedAmount) : '-',
            confirmed: c.hasSettlement ? formatWon(c.confirmedAmount) : '-',
            ratio: '-',
          }))
        : ongoing.map((r) => ({
            clinicId: r.clinicId,
            clinicName: r.clinicName,
            estimated: '-',
            confirmed: '-',
            ratio: '-',
          }));
    body = rows.length ? (
      <ResultTable ym={ym} rows={rows} />
    ) : (
      <p className="py-10 text-center text-body-14-r text-fg-tertiary">
        이 달에 실적이 있는 병의원이 없어요.
      </p>
    );
  }

  return (
    <section className="flex flex-col gap-6 rounded-20 bg-surface-white p-6">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center">
          <Button
            variant="text"
            size="small"
            aria-label="이전 달"
            disabled={!canPrev}
            onClick={() => onMove(-1)}
            leftIcon={<Icon name="page-left-solid-16" />}
          />
          <h2 className="text-subtitle-18-b text-fg-primary">{ymLabel(ym)}</h2>
          <Button
            variant="text"
            size="small"
            aria-label="다음 달"
            disabled={!canNext}
            onClick={() => onMove(1)}
            leftIcon={<Icon name="page-right-solid-16" />}
          />
        </div>
        {!loading && (
          <MonthBadge
            status={status}
            current={current}
            hasUnregistered={unregistered.length > 0}
          />
        )}
      </div>
      <div className={cn('flex flex-col gap-6')}>{body}</div>
    </section>
  );
};
